// Components
import { VALIDATION_ERROR_MESSAGE } from "../FormBuilder/ChangeEvents";
import moment from "moment";
import GroupNumberIcon from "../Partials/Icons/GroupNumberIcon";
import TraffickingIcon from "../Partials/Icons/TraffickingIcon";
// Component Library
import AppSDK from "@growthos/ui-app-sdk";
import { CELL_STATE, ColumnTemplate, TD_TYPE } from "@acadia-ui/datatable";
import DatePicker from "@acadia-ui/datepicker";
import Dropdown from "@acadia-ui/dropdown";
import Textbox from "@acadia-ui/textbox";
import { TOOLBAR_MODE } from "@acadia-ui/toolbar";
import { buildDropdownSearchText, filterDropdownOption, matchesDropdownOptionSearch } from "../Inputs/Dropdown/dropdownSearch";
// Stores
import BookFiltersStore from "../../Flux/Stores/BookFiltersStore";
import MasterCampaignsStore from "../../Flux/Stores/MasterCampaignsStore";
import AdSetDataStore from "../../Flux/Stores/AdSetDataStore";
import InsertionOrderDataStore from "../../Flux/Stores/InsertionOrderDataStore";
import LineItemDataStore from "../../Flux/Stores/LineItemDataStore";
import PackagesDataStore from "../../Flux/Stores/PackagesDataStore";
import PlacementDataStore from "../../Flux/Stores/PlacementDataStore";
import CreativesDataStore from "../../Flux/Stores/CreativesDataStore";
// Actions
import { updateAdSetsSheet } from "../../Flux/Actions/AdSetActions";
import { updateInsertionOrdersSheet } from "../../Flux/Actions/InsertionOrderActions";
import { updateLineItemsSheet } from "../../Flux/Actions/LineItemActions";
import { updatePackagesSheet } from "../../Flux/Actions/PackagesActions";
import { updateSheetData } from "../../Flux/Actions/PlacementActions";
import { updateCreativesSheet } from "../../Flux/Actions/CreativesActions";
import { parseContextData } from "../../Flux/Actions/BookFilterActions";
import { PlatformStatusIcon } from "../Partials/Icons/PlatformStatusIcon";
import { TOOLTIP_POSITION } from "@acadia-ui/tooltip";
import { EditOperation } from "@acadia-ui/theme";
import Status, { STATUS_TYPE } from "@acadia-ui/status";

export default class TableHelper {
	static HIERARCHY_LEVEL_CONSTANTS = {
		AdSet: {
			DataBucketName: "AdSetData",
			TaxonomyName: "AdSetName",
			IDName: "AdSetID",
			saveTable: updateAdSetsSheet,
			dataStore: AdSetDataStore
		},
		InsertionOrder: {
			DataBucketName: "InsertionOrderData",
			TaxonomyName: "InsertionOrderName",
			IDName: "InsertionOrderID",
			saveTable: updateInsertionOrdersSheet,
			dataStore: InsertionOrderDataStore
		},
		LineItem: {
			DataBucketName: "LineItemData",
			TaxonomyName: "LineItemName",
			IDName: "LineItemID",
			saveTable: updateLineItemsSheet,
			dataStore: LineItemDataStore
		},
		Package: {
			DataBucketName: "PackageData",
			TaxonomyName: "PackageName",
			IDName: "PackageID",
			saveTable: updatePackagesSheet,
			dataStore: PackagesDataStore
		},
		Placement: {
			DataBucketName: "PlacementData",
			TaxonomyName: "PlacementName",
			IDName: "PlacementID",
			saveTable: updateSheetData,
			dataStore: PlacementDataStore
		},
		Creative: {
			DataBucketName: "CreativeData",
			TaxonomyName: "CreativeName",
			IDName: "CreativeID",
			saveTable: updateCreativesSheet,
			dataStore: CreativesDataStore
		}
	};

	static NON_ARRAY_CONFIG_FIELDS = ["RegEx", "ChildData"];
	static DESCRIPTION_FIELD_NAMES = ["InsertionOrderName_Friendly", "PackageName_Friendly", "InsertionOrderDescription"];
	static PACKAGE_BASED_PLACEMENT_DISABLED_FIELDS = ["Rate Type", "Units", "Rate", "Site", "Publisher/ Site/ Vendor", "InName Site", "Partner"];
	static PROGRAMMATIC_BASED_PLACEMENT_DISABLED_FIELDS = ["Site", "Publisher/ Site/ Vendor", "InName Site", "Partner"];
	static SEARCH_ORDER_PLACEMENT_DISABLED_FIELDS = ["Rate Type", "Buy Type"];

	static GCM_PLACEMENT_DISABLED_CELLS = ["Dimensions", "Size", "PlacementSize", "InNameSite", "Publisher", "DisplayCompatibility", "PlacementCompatibility"];
	static GCM_PACKAGE_DISABLED_CELLS = ["InNameSite", "Publisher"];

	static TRAFFICKING_STATUS_CONSTANTS = {
		5: {
			message: "Available for Traffic Request",
			type: "success"
		},
		4: {
			message: "Match not found",
			type: "failed"
		},
		3: {
			message: "Revision Needed",
			type: "warning"
		},
		2: {
			message: "Traffic Request In Progress",
			type: "processing"
		},
		default: {
			message: "New Placements",
			type: "draft"
		}
	};

	// workaround for ErrorPaginator defect: if errors have been removed & our currentIndex > numAvailable,
	// click the paginator dom elements to decrement until this is no longer the case. It is not possible top do this another way currently.
	// static fixErrorPaginator = (hierarchyLevel) => {
	// 	const paginator = document.querySelector(`.gos-workbook__auditlevel-${hierarchyLevel?.toLowerCase?.()} .gos-toolbar__error-paginator`);
	// 	const buttonContainer = paginator?.querySelector(".gos-toolbar__button-container");

	// 	const [currentIndex, numAvailable] = buttonContainer?.innerText?.split(" of ") || [];

	// 	if (currentIndex > numAvailable) {
	// 		const decrementNTimes = currentIndex - numAvailable;
	// 		const decrementButton = buttonContainer?.querySelector("button:has(.icon-chevron-left)");

	// 		//click the first one without delay
	// 		decrementButton.click();
	// 		let count = 1;
	// 		const interval = setInterval(() => {
	// 			if (count >= decrementNTimes || !decrementButton) {
	// 				clearInterval(interval);
	// 				return;
	// 			}
	// 			decrementButton.click();
	// 			count++;
	// 		}, 100);
	// 	}
	// };

	static getDisabledColumnMap = (dataProvider, dataKey, isReadOnly, hierarchyLevel, config, metaData, gcmStatus, isGCMCampaign) => {
		const { Name } = dataKey;
		return dataProvider?.data?.reduce(
			(acc, rowData, i) => {
				const value = TableHelper.checkIfCellStateDisabled({ dataKey, hierarchyLevel, rowData, config, metaData, gcmStatus, isGCMCampaign });

				if (isReadOnly || value) {
					acc.default[`${Name}-${i}`] = CELL_STATE.DISABLED;
					acc.edit[`${Name}-${i}`] = CELL_STATE.DISABLED;
				}
				return acc;
			},
			{ default: {}, edit: {} }
		);
	};

	static createEditableColumn = ({
		dataKey,
		metaData,
		updateDataProvider,
		config,
		hierarchyLevel,
		freeformFields,
		dataProvider,
		isEditing,
		hasErrors,
		placementAPI,
		gcmStatus,
		navigateToChild,
		stickyState,
		isLastColumn,
		isUSCurrency,
		dataTableApi,
		getDragEditRevision
	}) => {
		const editProps = { children: "" };
		const isReadOnly = this.isReadOnlyEditCell({ bind: dataKey.Name, hierarchyLevel });

		if (dataKey.DataType === "Select") {
			editProps.isSearchable = true;
		} else if (dataKey.DataType === "Date") {
			if (metaData.StartDate) {
				editProps.minDate = new Date(metaData.StartDate);
			}
			if (metaData.EndDate) {
				editProps.maxDate = new Date(metaData.EndDate);
			}
		}
		const isGCMCampaign = metaData?.PlatformID == 1;
		const isAnyCellDisabled = dataProvider?.data?.some((row) => {
			return (
				this.checkIfCellDisabled({
					dataKey,
					hierarchyLevel,
					rowData: row
				}) ||
				(gcmStatus && isGCMCampaign && this.disableCellIfGCMPushed({ dataKey, rowData: row, hierarchyLevel }))
			);
		});

		const targetedColumns = ["PlannedUnits", "Cost", "Rate", "RateType"];
		const hasValidPushStatus = PlacementDataStore?.getData?.()?.some?.((datum) => ["True", "False"]?.includes(datum?.IsPrismaBudgetLocked));
		const showLockIcon = placementAPI && metaData.PlatformID === 5 && targetedColumns.includes(dataKey.Name) && hasValidPushStatus;
		const isLocked = PlacementDataStore?.getData?.()?.some?.((datum) => datum?.IsPrismaBudgetLocked == "True") || false;
		const columnSettings =
			dataKey.Name === "IsDoNotUse"
				? { columnSettings: { label: "Do Not Use", canHide: true, defaultHidden: dataKey.defaultHidden, minWidth: true, defaultSticky: true } }
				: {};
		const readOnlyClassName = isReadOnly ? "pmui-readonly-edit-cell" : "";

		// util function called getDataIndex that returns the index in dataProvider.data of a row, based on it's UniqueID property
		const getDataIndex = (row) => dataProvider?.data?.findIndex?.(({ UniqueID }) => row?.UniqueID === UniqueID) ?? -1;

		const onTextboxChange = (e, rowData) => {
			// Perform text field validations here on e.target.value
			let validatedValue = e.target.value;
			if (dataKey.ValidationString) {
				// Skip legacy stripping — pattern attribute handles validation
			} else if (freeformFields.includes(dataKey.Name)) {
				if (config?.RegEx?.length) {
					let formattedValidation = config?.RegEx[0].substring(config?.RegEx[0].indexOf("["), config?.RegEx[0].lastIndexOf("]") + 1);
					formattedValidation = formattedValidation.substring(0, 1) + "^" + formattedValidation.substring(1, formattedValidation.length);
					if (formattedValidation.indexOf("\\\\\\") !== -1) {
						formattedValidation =
							formattedValidation.substring(0, formattedValidation.indexOf("\\\\\\")) +
							"\\\\\\" +
							formattedValidation.substring(formattedValidation.indexOf("\\\\\\") + 1, formattedValidation.length);
					}

					validatedValue = validatedValue.replace(new RegExp(formattedValidation, "g"), "");
				} else {
					validatedValue = validatedValue.replace(/[^a-zA-Z0-9-\\/| +]/g, "");
				}
			} else {
				// eslint-disable-next-line no-useless-escape
				validatedValue = validatedValue.replace(/[^a-zA-Z0-9 \-]/g, "");
			}
			updateDataProvider(validatedValue, getDataIndex(rowData), dataKey.Name);
		};

		return (
			<ColumnTemplate
				key={`${dataKey.Name}Column`}
				className={readOnlyClassName}
				isEditable={!isReadOnly}
				bind={dataKey.Name}
				data-label={dataKey.Label}
				isDraggable={!isReadOnly && !isAnyCellDisabled} // negatively interferes with cells disabled in onEdit (parent/child)
				// isDisabled={TableHelper.getDisabledColumnMap(dataProvider, dataKey, isReadOnly, hierarchyLevel, config, metaData, gcmStatus, isGCMCampaign)}
				isSortable={true}
				{...(dataKey.DataType === "Long" ? { align: "right" } : {})}
				// hasRenderCache={false}
				minWidth={true}
				// truncateContent={true}
				sticky={stickyState?.[dataKey.Name] || false}
				{...columnSettings}
				// shouldEditOnClick={false} //this causes unacceptable lag

				// navigateToChild is relevant for package and io parent deeplinking fields.
				{...(!navigateToChild
					? {}
					: {
							onRender: (bindData, rowData, rowIndex) => {
								if (rowData.ChildCount && rowData.ChildCount !== "0" && !isEditing) {
									return (
										<div
											className="parentCell"
											onClick={() => {
												navigateToChild?.(rowData);
											}}
										>
											{bindData} ({rowData.ChildCount})
										</div>
									);
								}
								return <>{bindData}</>;
							}
						})}
				{...(dataKey.DataType === "Select"
					? {
							type: TD_TYPE.OPTIONS,
							onEdit: (bindData, rowData, rowIndex) => {
								const options = this.calculateDropdownOptions({
									dataKey,
									hierarchyLevel,
									rowData,
									config,
									metaData
								});
								const usesRateTypeSearch = dataKey.Name === "RateType";
								const filterDropdownValues = (dropdownOptions, searchValue) => {
									if (!searchValue) {
										return dropdownOptions;
									}

									if (usesRateTypeSearch) {
										return dropdownOptions.filter((option) => matchesDropdownOptionSearch(option, searchValue));
									}

									const normalizedQuery = searchValue.toLowerCase();
									return dropdownOptions.filter(({ label }) => label?.toLowerCase()?.includes(normalizedQuery));
								};
								const matchingValue = bindData
									? options?.dropdownOptions?.find((option) => {
											return option.label === bindData;
										})
									: null;

								const cellDisabled =
									this.checkIfCellDisabled({
										dataKey,
										hierarchyLevel,
										rowData
									}) ||
									options.missingParentValue ||
									(gcmStatus && isGCMCampaign && this.disableCellIfGCMPushed({ dataKey, rowData, hierarchyLevel })) ||
									false;

								return (
									<Dropdown
										className="table-dropdown"
										// options={options?.dropdownOptions}
										// disabled={cellDisabled}
										// value={matchingValue}
										defaultValue={matchingValue}
										onChange={(value) => {
											// dataProvider.editStack.push(
											// 	new EditOperation({
											// 		rowId: rowData.UniqueID,
											// 		rowIndex,
											// 		key: dataKey.Name,
											// 		value: value?.label
											// 	})
											// );
											const newValue = value?.label;
											updateDataProvider(newValue, getDataIndex(rowData), dataKey.Name, true, dataKey.Name === "RateType");
										}}
										isClearable={true}
										asyncOptions={{
											pageSize: 50,
											requestValues: ({ searchValue, page }) => {
												const promise = new Promise((resolve) => {
													setTimeout(() => {
														const dropdownOptions = options?.dropdownOptions || [];

														const values = filterDropdownValues(dropdownOptions, searchValue);
														resolve({
															totalCount: values.length,
															values: values?.slice(page * 50, (page + 1) * 50)
														});
													}, 0);
												});
												return promise;
											}
										}}
										{...(usesRateTypeSearch ? { filterOption: filterDropdownOption } : {})}
										{...editProps}
										options={[]}
									>
										table dropdown
									</Dropdown>
								);
							}
						}
					: dataKey.DataType === "Date"
						? {
								type: TD_TYPE.DATE,
								onEdit: (bindData, rowData, rowIndex) => {
									return (
										<DatePicker
											value={bindData ? new Date(bindData) : null}
											defaultValue={new Date()}
											{...(isLastColumn ? { popperPlacement: "bottom-end" } : {})}
											onChange={(newValue) => {
												const newDate = newValue?.toLocaleDateString("en-US", { day: "2-digit", month: "2-digit", year: "numeric" }) || "";
												updateDataProvider(newDate, getDataIndex(rowData), dataKey.Name);
											}}
											{...editProps}
										/>
									);
								}
							}
						: dataKey.DataType === "String"
							? {
									type: TD_TYPE.TEXT,
									onEdit: (bindData, rowData, rowIndex) => {
										let maxLength = 25;
										if (config?.RegEx?.length && freeformFields.includes(dataKey.Name)) {
											maxLength = parseInt(config?.RegEx[1]);
										}
										return (
											<Textbox
												// The editor is uncontrolled, so a drag that lands on a cell still being edited only remounts it — and
												// therefore only picks up the dragged value — when this key changes.
												key={`${rowData?.UniqueID}-${dataKey.Name}-${getDragEditRevision?.(rowData, dataKey.Name) ?? 0}`}
												defaultValue={bindData}
												onFocus={(e) => onTextboxChange(e, rowData)}
												onBlur={(e) => onTextboxChange(e, rowData)}
												onChange={(e, val) => {
													updateDataProvider(val, getDataIndex(rowData), dataKey.Name);
												}}
												maxLength={maxLength}
												pattern={dataKey.ValidationString || undefined}
												error={dataKey.ValidationString ? VALIDATION_ERROR_MESSAGE : undefined}
												// disabled={this.checkIfCellDisabled({
												// 	dataKey,
												// 	hierarchyLevel,
												// 	rowData
												// })}
											>
												table textbox
											</Textbox>
										);
									}
								}
							: dataKey.Name === "Rate"
								? {
										type: isUSCurrency ? TD_TYPE.CURRENCY : TD_TYPE.NUMBER,
										onEdit: (bindData, rowData, rowIndex) => {
											return (
												<Textbox
													type="currency"
													// step="0.00001"
													// typeDirection={"left"}
													onBlur={(e) => {
														updateDataProvider(e.target.value, getDataIndex(rowData), dataKey.Name, false, true);
													}}
													// disabled={this.checkIfCellDisabled({
													// 	dataKey,
													// 	hierarchyLevel,
													// 	rowData
													// })}
													icon={isUSCurrency ? undefined : null}
												>
													table textbox
												</Textbox>
											);
										}
									}
								: dataKey.Name === "PlannedUnits"
									? {
											type: TD_TYPE.NUMBER,
											onEdit: (bindData, rowData, rowIndex) => {
												return (
													<Textbox
														type="number"
														hideSpinners={true}
														onMouseEnter={(e) => {
															e.target.title = "";
														}}
														onBlur={(e) => {
															const integerValue = e.target.value.trim() === "" ? "0" : parseInt(e.target.value).toString();
															updateDataProvider(integerValue, getDataIndex(rowData), dataKey.Name, false, true);
														}}
														// disabled={this.checkIfCellDisabled({
														// 	dataKey,
														// 	hierarchyLevel,
														// 	rowData
														// })}
													>
														table textbox
													</Textbox>
												);
											}
										}
									: dataKey.DataType === "Currency"
										? {
												type: isUSCurrency ? TD_TYPE.CURRENCY : TD_TYPE.NUMBER,
												onEdit: (bindData, rowData, rowIndex) => {
													return (
														<Textbox
															type="currency"
															// step="0.01"
															// typeDirection={"left"}
															onBlur={(e) => {
																updateDataProvider(e.target.value, getDataIndex(rowData), dataKey.Name);
															}}
															// disabled={this.checkIfCellDisabled({
															// 	dataKey,
															// 	hierarchyLevel,
															// 	rowData
															// })}
															icon={isUSCurrency ? undefined : null}
														>
															table textbox
														</Textbox>
													);
												}
											}
										: {})}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					{showLockIcon && <i className={`icon-${isLocked ? "lock" : "unlock"}`} />}
					{dataKey.Label}
				</div>
			</ColumnTemplate>
		);
	};

	static calculateDropdownOptions = ({ dataKey, hierarchyLevel, rowData, config, metaData }) => {
		let missingParentValue = false;
		let dropdownOptions = dataKey.Options.map((dataValue) => {
			const dropdownOption = { ...dataValue, value: dataValue.OptionID, label: dataValue.OptionLabel };
			if (dataKey.Name === "RateType") {
				dropdownOption.searchText = buildDropdownSearchText(dataValue);
			}
			return dropdownOption;
		});
		if (dataKey.ParentName) {
			let parentDataValueID = null;
			const dataKeysInConfig = [...config.TaxonomyData, ...config.MetaData, ...config[this.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel].DataBucketName]];
			const parentDataKeyInConfig = dataKeysInConfig.find((configKey) => {
				return configKey.Name === dataKey.ParentName;
			});
			if (!parentDataKeyInConfig) {
				const activationConfig = MasterCampaignsStore.getActivationConfig();
				const masterCampaignData = MasterCampaignsStore.getData().find((masterCampaign) => {
					return masterCampaign.MasterCampaignID == metaData.MasterCampaignID;
				});
				if (masterCampaignData) {
					let campaignData = null;
					Object.values(masterCampaignData.ActivationCampaigns).find((campaignsByPlatform) => {
						campaignData = campaignsByPlatform.find((campaign) => {
							return campaign.CampaignID == metaData.CampaignUniqueIDCode;
						});
					});
					if (campaignData && campaignData[dataKey.ParentName] && activationConfig?.TaxonomyData && activationConfig?.MetaData) {
						const dataKeysInActivationConfig = [...activationConfig.TaxonomyData, ...activationConfig.MetaData];
						const parentDataKeyInActivationConfig = dataKeysInActivationConfig.find((configKey) => {
							return configKey.Name === dataKey.ParentName;
						});
						parentDataValueID = parentDataKeyInActivationConfig?.Options?.find((option) => {
							return option.OptionLabel == campaignData[dataKey.ParentName];
						})?.OptionID;
					}
				}
			}
			const parentInCampaignConfig = Boolean(parentDataValueID);
			if (parentDataKeyInConfig && rowData[dataKey.ParentName]) {
				parentDataValueID = parentDataKeyInConfig?.Options?.find((option) => {
					return option.OptionLabel == rowData[dataKey.ParentName];
				})?.OptionID;
			}
			// Only apply CBL validation if the parent is found in either the hierarchy level config or the campaign level config
			if (parentDataKeyInConfig || parentInCampaignConfig) {
				if (parentDataValueID) {
					const optionsWithMatchingParent = dropdownOptions.filter((option) => {
						const parsedOptions = option?.ParentOptionID?.split?.(",");
						return parsedOptions.includes(parentDataValueID);
					});
					const orphanedOptions = dropdownOptions.filter((option) => {
						return !option.ParentOptionID;
					});
					// Specific rule for Packages/Placements with BuyType "Search" or "Social". Don't include orphaned options in the BuyCategory dropdown
					const isSearchOrSocial = ["Search", "Social"].includes(rowData?.BuyType) && dataKey.Name === "BuyCategory";
					dropdownOptions = optionsWithMatchingParent.length === 0 ? dropdownOptions : [...optionsWithMatchingParent, ...(!isSearchOrSocial ? orphanedOptions : [])];
				} else if (parentDataKeyInConfig && !parentDataValueID) {
					// Only disable the cell if there is no parentDataValueID and the parent key is in the hierarchy config. If the parent is only in the campaign level config, we don't need to disable the cell
					missingParentValue = true;
				}
			}
		}
		if (dataKey.Name === "BuyType") {
			dropdownOptions = dropdownOptions.filter((option) => {
				return option.label !== "Search";
			});
		}
		if (dataKey.Name === "IsDoNotUse") {
			dropdownOptions = dropdownOptions.filter((option) => {
				return option.OptionLabel !== "STANDALONE";
			});
		}
		return { missingParentValue, dropdownOptions };
	};

	static checkIfCellDisabled = ({ dataKey, hierarchyLevel, rowData }) => {
		// prettier-ignore
		return (
			hierarchyLevel === "Placement" && 
			((rowData.PlacementParentPackageType === "Standard" && this.PACKAGE_BASED_PLACEMENT_DISABLED_FIELDS.includes(dataKey.Label)) 
			|| (rowData.BuyType === "Search" && this.SEARCH_ORDER_PLACEMENT_DISABLED_FIELDS.includes(dataKey.Label)) 
			|| (rowData.PlacementParentPackageType === "Programmatic" && this.PROGRAMMATIC_BASED_PLACEMENT_DISABLED_FIELDS.includes(dataKey.Label)))
		);
	};

	static checkIfCellStateDisabled = ({ dataKey, hierarchyLevel, rowData, config, metaData, gcmStatus, isGCMCampaign }) => {
		const missingParentValue =
			dataKey?.DataType === "Select" &&
			this.calculateDropdownOptions({
				dataKey,
				hierarchyLevel,
				rowData,
				config,
				metaData
			}).missingParentValue;

		return (
			missingParentValue ||
			this.checkIfCellDisabled({ dataKey, hierarchyLevel, rowData }) ||
			(gcmStatus && isGCMCampaign && this.disableCellIfGCMPushed({ dataKey, rowData, hierarchyLevel }))
		);
	};

	static isReadOnlyEditCell = ({ bind, hierarchyLevel }) => {
		return (
			bind === "Cost" ||
			((hierarchyLevel === "Placement" || hierarchyLevel === "LineItem") && bind === "ParentDescription") ||
			(hierarchyLevel === "Package" && bind === "InNameSite")
		);
	};

	static refreshDisabledCellStates = ({ cellStateManager, dataProvider, columns, dataKeysByBind, hierarchyLevel, config, gcmStatus, metaData, errorCellKeys }) => {
		const filteredDataSet = dataProvider?.filteredData !== undefined ? dataProvider?.filteredData : dataProvider?.data;
		const canonicalRowsById = new Map((dataProvider?.data || []).map((row) => [row.UniqueID, row]));
		const dataSet = filteredDataSet?.map((row) => canonicalRowsById.get(row.UniqueID) || row);
		const isGCMCampaign = metaData?.PlatformID == 1;

		if (!cellStateManager || !dataSet?.length || !columns?.length) {
			return;
		}

		cellStateManager.resetCellStates();
		const updates = {
			[TOOLBAR_MODE.DEFAULT]: [],
			[TOOLBAR_MODE.EDIT]: []
		};

		dataSet.forEach((row, dataIndex) => {
			const rowId = row?.UniqueID;
			columns.forEach((column) => {
				const { bind, editHighlighting } = column?.props || {};
				const dataKey = dataKeysByBind[bind];

				if (!bind) {
					return;
				}

				const isReadOnly = this.isReadOnlyEditCell({ bind, hierarchyLevel });
				const isEditDisabled =
					dataKey && (isReadOnly || this.checkIfCellStateDisabled({ dataKey, hierarchyLevel, rowData: row, config, metaData, gcmStatus, isGCMCampaign }));
				const legacyDisabledProp = column?.props?.isDisabled;
				const update = {
					rowIndex: dataIndex,
					columnKey: bind,
					state: CELL_STATE.DEFAULT
				};

				if (isEditDisabled || legacyDisabledProp === true || legacyDisabledProp?.[`${rowId}-${bind}`] === true) {
					updates[TOOLBAR_MODE.DEFAULT].push({ ...update, state: CELL_STATE.DISABLED });
					updates[TOOLBAR_MODE.EDIT].push({ ...update, state: CELL_STATE.DISABLED });
				} else if (editHighlighting === true) {
					updates[TOOLBAR_MODE.DEFAULT].push(update);
					updates[TOOLBAR_MODE.EDIT].push({ ...update, state: CELL_STATE.HIGHLIGHT });
				} else if (errorCellKeys.has(`${rowId}-${bind}`)) {
					updates[TOOLBAR_MODE.DEFAULT].push(update);
					updates[TOOLBAR_MODE.EDIT].push({ ...update, state: CELL_STATE.ERROR });
				} else {
					updates[TOOLBAR_MODE.DEFAULT].push(update);
					updates[TOOLBAR_MODE.EDIT].push(update);
				}
			});
		});

		Object.entries(updates).forEach(([mode, modeUpdates]) => {
			if (modeUpdates.length > 0) {
				cellStateManager.updateMultipleCellStates({ mode, updates: modeUpdates });
			}
		});
	};

	static disableCellIfGCMPushed = ({ dataKey, rowData, hierarchyLevel }) => {
		if (
			rowData.ExternalSystemStatusID == 3 &&
			((hierarchyLevel === "Package" && this.GCM_PACKAGE_DISABLED_CELLS.includes(dataKey?.Name)) ||
				(hierarchyLevel === "Placement" && this.GCM_PLACEMENT_DISABLED_CELLS.includes(dataKey?.Name)))
		) {
			return true;
		}
		return false;
	};

	static formatPostObj = ({ data, config, hierarchyLevel, metaData, auditLevelID }) => {
		// Need to remove unedited rows before creating the post obj. If it only has a single entry, that means it only has a UniqueID and no other fields filled,
		// meaning it is an untouched row
		const dataWithEmptyLinesRemoved = data.filter((row) => {
			return Object.keys(row).length !== 1;
		});
		const oldFilters = BookFiltersStore.getChoicesState();

		const filters = parseContextData(AppSDK.getContext(), AppSDK.getContextId());
		const filterValues = {
			MasterAgencyDivisionName: filters.agency.name,
			MasterBusinessUnitName: filters.client.name,
			MasterCountryCode: filters.country.name,
			ContextID: AppSDK.getContextId(),
			PlatformID: oldFilters.platform.value,
			AuditLevelID: auditLevelID,
			MasterRegionCode: filters.region.name,
			CampaignUniqueIDCode: MasterCampaignsStore.getParentCampaignUniqueID()
			// MasterClientID: filters.clients.value,
			// PlatformID: filters.platform.value,
			// MasterAgencyDivisionName: "",
			// MasterBusinessUnitName: "",
			// MasterCountryCode: "",
			// PlatformID: "",
			// AuditLevelID: ""
			// MasterRegionCode: ""
			// CampaignUniqueIDCode: ""
		};
		const tablePostObj = [];
		const { DataBucketName, IDName } = this.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel];
		dataWithEmptyLinesRemoved.forEach((row) => {
			const formattedRowData = this.buildPostObjBuckets(row, Object.entries(config), DataBucketName, hierarchyLevel);
			if (row[IDName]) {
				formattedRowData[DataBucketName][IDName] = row[IDName];
			}

			formattedRowData[DataBucketName].MasterCampaignID = row.MasterCampaignID || metaData.MasterCampaignID;
			formattedRowData[DataBucketName].CampaignID = metaData.CampaignUniqueIDCode;
			if (formattedRowData[DataBucketName].PlatformSiteID !== null) {
				let siteID = null;
				config[DataBucketName].forEach((dataConfig) => {
					if (dataConfig.Name === "PlatformSiteID") {
						dataConfig.Options.forEach((site) => {
							if (site.OptionLabel === formattedRowData[DataBucketName].PlatformSiteID) {
								siteID = site.OptionID;
							}
						});
					}
				});
				if (siteID !== null) {
					formattedRowData[DataBucketName].PlatformSiteID = siteID;
				}
			}
			tablePostObj.push({ ...formattedRowData, ...filterValues });
		});
		return tablePostObj;
	};

	static buildPostObjBuckets = (rowData, config, hiearachyDataBucketName, hierarchyLevel) => {
		const rowTaxonomyData = rowData.TaxonomyData ? JSON.parse(rowData.TaxonomyData) : [];
		const rowMetaData = rowData.MetaData ? JSON.parse(rowData.MetaData) : [];
		const postObj = {};
		// Create Iniital Bucket Arrays
		for (const dataBucket of config) {
			if (dataBucket[0] !== "ParentData") {
				if (dataBucket[0] === "TaxonomyData" || dataBucket[0] === "MetaData") {
					postObj[`${dataBucket[0]}`] = [];
				} else {
					postObj[`${dataBucket[0]}`] = {};
				}
			}
		}
		for (const item in rowData) {
			if (Object.hasOwn(rowData, item)) {
				const itemKey = item;
				// Check all Buckets from the Config Obj
				for (const dataBucket of config) {
					// Process all the data buckets except Parent Data.
					if (dataBucket[0] !== "ParentData" && Array.isArray(dataBucket[1])) {
						for (const column of dataBucket[1]) {
							if (column.Name === itemKey.toString()) {
								// Don't send an empty string to the api for Date fields, the returned error will not be in the expected format for the ErrorNavigation
								if (column.DataType === "Date" && !rowData[item]) {
									delete rowData[item];
								} else {
									// Based on DataType do stuff
									if (dataBucket[0] === "TaxonomyData" || dataBucket[0] === "MetaData") {
										if (rowData[item]) {
											const reformattedValue = this.reformatTaxonomyValue(
												column.DataType,
												column.Options,
												column.ID,
												rowData[item] ? rowData[item] : "",
												rowTaxonomyData,
												rowMetaData,
												column.Label
											);

											// avoids pushing archived taxonomy values with no ID or freeform value
											if (reformattedValue !== undefined) {
												postObj[`${dataBucket[0]}`].push(reformattedValue);
											}
										}
									} else if (itemKey === "Rate" || itemKey === "PlannedUnits") {
										if (rowData[item] !== "") {
											postObj[`${dataBucket[0]}`][`${itemKey}`] = rowData[item];
										}
									} else {
										postObj[`${dataBucket[0]}`][`${itemKey}`] = rowData[item];
									}
								}
							}
						}
					}
				}
			}
		}

		if (postObj[hiearachyDataBucketName]) {
			postObj[hiearachyDataBucketName].UniqueID = rowData.UniqueID;
			if (hierarchyLevel === "LineItem") {
				postObj.LineItemData.InsertionOrderID = rowData.InsertionOrderID;
			}

			if (["Placement", "AdSet", "LineItem"]?.includes(hierarchyLevel) && rowData.IsDoNotUse) {
				postObj[`${hierarchyLevel}Data`].IsDoNotUse = rowData.IsDoNotUse;
			}
		}
		if (hierarchyLevel === "Placement") {
			if (rowData.PackageID) {
				postObj.ParentData = { ParentUniqueIdCode: rowData.PackageID };
			}
			if (rowData.PackageType === "SearchOrder") {
				postObj[hiearachyDataBucketName].PackageType = "Search Order";
			}
			if (rowData.PlatformPlacementPK) {
				// try catch to avoid bad data issue where PK is a string...
				try {
					const prismaPlatformPK = JSON.parse(rowData.PlatformPlacementPK)?.find(({ MasterPlatformID }) => MasterPlatformID == 5);
					if (prismaPlatformPK?.PlatformPK) {
						postObj.PlatformPlacementPK = prismaPlatformPK?.PlatformPK;
					}
				} catch (e) {
					// noop
					console.warn("Error parsing Placement Platform PK", e);
				}
			}
		}
		if (hierarchyLevel === "Package") {
			postObj[hiearachyDataBucketName].PackageType = rowData.PackageType;
		}
		return postObj;
	};

	static reformatTaxonomyValue(dataType, dataOptions, dataID, selectedDataValue, taxonomyData, metaData, label) {
		if (dataType === "Select") {
			// Match the 'string' to the Options object to get ID

			const matchingOption = dataOptions.filter((option) => option.OptionLabel === selectedDataValue.toString());
			if (matchingOption[0] !== undefined) {
				const optionID = matchingOption[0].OptionID;
				return {
					dataKeyID: dataID,
					dataValue: {
						dataValueID: optionID
					}
				};
			}
			if (matchingOption[0] === undefined) {
				let dataValueID = null;
				taxonomyData.forEach((taxonomyField) => {
					if (taxonomyField.DataKeyShortDescr === label && !taxonomyField.IsFreeFormValue) {
						dataValueID = taxonomyField.DataValue?.DataValueID;
					}
				});
				if (!dataValueID) {
					metaData.forEach((metaDataField) => {
						if (metaDataField.DataKeyShortDescr === label && !metaDataField.IsFreeFormValue) {
							dataValueID = metaDataField.DataValue?.DataValueID;
						}
					});
				}
				if (dataValueID) {
					const changedMatchingOption = dataOptions.filter((option) => option.OptionID === dataValueID.toString());
					if (changedMatchingOption[0] !== undefined) {
						const optionID = changedMatchingOption[0].OptionID;
						return {
							dataKeyID: dataID,
							dataValue: {
								dataValueID: optionID
							}
						};
					}
				}
			}
		} else {
			return {
				dataKeyID: dataID,
				dataValueIfFreeForm: selectedDataValue
			};
		}
	}

	static handleErrorNavigation = (currentData, errorBundle, hierarchyLevel) => {
		const tableBody = document.querySelector(`#${hierarchyLevel}__table .gos-dt-tbody`);
		const errorNavigation = [];
		Object.keys(errorBundle)?.forEach((rowUniqueID) => {
			if (errorBundle[rowUniqueID].Result == false) {
				const matchingRowIndex = currentData.indexOf(
					currentData.find((row) => {
						return row.UniqueID == rowUniqueID;
					})
				);
				if (matchingRowIndex != -1) {
					const inputErrors = errorBundle[rowUniqueID]?.Object?.InputErrors;
					const columns = Array.from(Array.from(tableBody.children)?.[matchingRowIndex]?.children);
					inputErrors.forEach((errorMessage) => {
						const errorObject = Object.entries(errorMessage)[0];
						const matchingColumn = columns.find((column) => {
							return column.getAttribute("data-bind") == errorObject[0];
						});
						if (matchingColumn) {
							const matchingColumnIndex = columns.indexOf(matchingColumn);
							const existingError = errorNavigation.find((error) => {
								return error.row === matchingRowIndex && error.column === matchingColumnIndex;
							});
							if (existingError) {
								existingError.message += ` ${errorObject[1]}`;
							} else {
								errorNavigation.push({
									row: matchingRowIndex,
									column: matchingColumnIndex,
									columnName: errorObject[0],
									message: errorObject[1]
								});
								matchingColumn.classList.add("error_cell");
							}
						}
					});
				}
			}
		});
		errorNavigation.sort((a, b) => {
			if (a.row < b.row) {
				return -1;
			}
			if (a.row > b.row) {
				return 1;
			}
			if (a.row === b.row) {
				if (a.column < b.column) {
					return -1;
				}
				return 1;
			}
			return 0;
		});
		return errorNavigation;
	};

	// static reapplyErrorStyling = (errorsArray, hierarchyLevel) => {
	// 	errorsArray.forEach((error) => {
	// 		try {
	// 			const cell = this.getSpecificCell(hierarchyLevel, error.row, error.columnName);
	// 			cell?.classList.add("error_cell");
	// 		} catch (e) {
	// 			console.log(e);
	// 		}
	// 	});
	// };

	// static clearErrors = (hierarchyLevel) => {
	// 	const cellsWithErrors = document.querySelectorAll(`#${hierarchyLevel}__table .error_cell`);
	// 	for (const cell of cellsWithErrors) {
	// 		cell.classList.remove("error_cell");
	// 	}
	// };

	static CBLCascadingDelete = (data, rowIndex, columnNames, dataKeysInConfig) => {
		const directChildrenDataKeys = dataKeysInConfig
			.filter((dataKey) => {
				return columnNames.includes(dataKey.ParentName);
			})
			.map((dataKeyWithParent) => {
				return dataKeyWithParent.Name;
			});
		if (directChildrenDataKeys.length !== 0) {
			directChildrenDataKeys.forEach((childKeyName) => {
				delete data[rowIndex][childKeyName];
			});
			this.CBLCascadingDelete(data, rowIndex, directChildrenDataKeys, dataKeysInConfig);
		} else {
		}
	};

	static createDuplicates = (data, rows, duplicateCount) => {
		const duplicatedRows = [];
		rows.forEach((row) => {
			for (let i = 0; i < duplicateCount; i++) {
				duplicatedRows.push(JSON.parse(JSON.stringify(row)));
			}
		});
		const dataWithDuplicatedRows = [...duplicatedRows, ...data];
		dataWithDuplicatedRows.forEach((row, index) => {
			if (!row.UniqueID) {
				row.UniqueID = `duplicate${index}`;
				row.ChildCount = "0";
			}
		});
		return dataWithDuplicatedRows;
	};

	static handleDataMultiplier = (fieldNameObject, fieldValuesObject, rowToMultiply, allRows, totalCount, hierarchyLevel) => {
		const dataWithMultipliedRows = allRows;
		const rowData = rowToMultiply;
		Object.values(fieldNameObject).forEach((field) => {
			delete rowData[field];
		});
		const duplicateObj = [];
		for (let i = 0; i < totalCount; i++) {
			const newRow = JSON.parse(JSON.stringify(rowData));
			newRow.UniqueID = `duplicate${i}`;
			delete newRow[this.HIERARCHY_LEVEL_CONSTANTS[hierarchyLevel].TaxonomyName];
			duplicateObj.push(newRow);
		}

		const optionsInEachField = [];
		const counter = [];
		const numberOfFields = Object.keys(fieldValuesObject).length;
		Object.keys(fieldValuesObject).forEach((key) => {
			optionsInEachField.push(fieldValuesObject[key].length);
			counter.push(0);
		});
		for (let n = 0; n < totalCount; n++) {
			for (let i = 0; i < numberOfFields; i++) {
				if (typeof fieldValuesObject[i][counter[i]] === "object") {
					duplicateObj[n][fieldNameObject[i]] = fieldValuesObject[i][counter[i]].label;
				} else if (typeof fieldValuesObject[i][counter[i]] === "string") {
					duplicateObj[n][fieldNameObject[i]] = fieldValuesObject[i][counter[i]];
				}
			}

			for (let x = numberOfFields - 1; x >= 0; x--) {
				if (counter[x] + 1 < optionsInEachField[x]) {
					counter[x] += 1;
					break;
				}
				counter[x] = 0;
			}
		}
		return [...duplicateObj, ...dataWithMultipliedRows];
	};

	// static navigateToError = (errorLocation, hierarchyLevel) => {
	// 	document.querySelector(`#${hierarchyLevel}__table .gos-dt-tbody`).children[errorLocation.row].children[errorLocation.column].scrollIntoView();
	// 	this.highlightCell(hierarchyLevel, errorLocation.row, errorLocation.columnName);
	// };

	// static highlightCell = (hierarchyLevel, rowIndex, columnName) => {
	// 	const highlightedCells = document.querySelectorAll(`#${hierarchyLevel}__table .highlight_cell`);
	// 	for (const cell of highlightedCells) {
	// 		cell.classList.remove("highlight_cell");
	// 	}

	// 	const cell = this.getSpecificCell(hierarchyLevel, rowIndex, columnName);
	// 	cell?.classList.add("highlight_cell");
	// };

	static clearHighlightedCells = (hierarchyLevel) => {
		const highlightedCells = document.querySelectorAll(`#${hierarchyLevel}__table .highlight_cell`);
		for (const cell of highlightedCells) {
			cell.classList.remove("highlight_cell");
		}
		const outlinedCells = document.querySelectorAll(`#${hierarchyLevel}__table .outline_cell`);
		for (const cell of outlinedCells) {
			cell.classList.remove("outline_cell");
		}
	};

	static highlightCell = (hierarchyLevel, uniqueID, columnName) => {
		const cell = this.getSpecificCell(hierarchyLevel, uniqueID, columnName);
		cell?.classList.add("highlight_cell");
	};

	static visuallyReplaceData = (hierarchyLevel, rowNumber, columnName, newValue) => {
		const tableBody = document.querySelector(`#${hierarchyLevel}__table .gos-dt-tbody`);
		const columns = Array.from(Array.from(tableBody.children)?.[rowNumber]?.children);
		const matchingColumn = columns.find((column) => {
			return column.getAttribute("data-bind") == columnName;
		});
		matchingColumn.firstChild.innerHTML = newValue;
	};

	static outlineCell = (hierarchyLevel, uniqueID, columnName) => {
		const outlinedCells = document.querySelectorAll(`#${hierarchyLevel}__table .outline_cell`);
		for (const cell of outlinedCells) {
			cell.classList.remove("outline_cell");
		}
		const cell = this.getSpecificCell(hierarchyLevel, uniqueID, columnName);
		cell?.classList.add("outline_cell");
		cell?.scrollIntoView(false);
	};

	// static toggleCheckboxOnSelectAll = (hierarchyLevel, checked, rowsWithData) => {
	// 	const tableBody = document.querySelector(`#${hierarchyLevel}__table .gos-dt-tbody`);
	// 	const allRows = Array.from(Array.from(tableBody.children));
	// 	allRows.forEach((row, index) => {
	// 		if (rowsWithData.includes(index)) {
	// 			const checkbox = row.querySelector(".gos-chk input");
	// 			checkbox.checked = checked;
	// 		}
	// 	});
	// };

	static getSpecificCell = (hierarchyLevel, uniqueID, columnName) => {
		try {
			const tableBody = document.querySelector(`#${hierarchyLevel}__table .gos-dt-tbody`);
			const matchingRow = Array.from(tableBody.children).find((row) => {
				return row.querySelector(".gos-chk input")?.getAttribute("value") === uniqueID;
			});
			const columns = Array.from(matchingRow?.children);
			const specificCell = columns.find((column) => {
				return column.getAttribute("data-bind") == columnName;
			});
			return specificCell;
		} catch (e) {
			// console.log(e);
		}
	};

	static createExtraColumns = (columns, hierarchyLevel, extraColumns, metaData, enabledPlatformSync, buildDNU, stickyState) => {
		const findColumn = (target) => extraColumns.find(({ id }) => id == target);
		const findColumnByName = (targetName) => extraColumns.find(({ name }) => name == targetName);

		if (hierarchyLevel === "Placement") {
			const prismaSynced =
				metaData.PlatformID === 5 && // indicates this is prisma platform
				enabledPlatformSync.placementAPI === true && // indicates that the API is enabled at a client or campaign level
				metaData.ExternalSystemStatusID !== 5; // indicates that the campaign is not marked as "Not Eliible for Syncing"

			if (prismaSynced) {
				if (findColumn("PlatformStatus")) {
					columns.push(this.returnSyncStatusColumn("Prisma Status", !findColumn("PlatformStatus")?.selected, false, stickyState?.ExternalSystemStatusID));
				}
				if (findColumn("PrismaID")) {
					columns.push(this.returnPlatformPKColumn("Prisma ID", "PlatformPlacementPK", 5, !findColumn("PrismaID")?.selected, stickyState?.PlatformPlacementPK));
				}
			}
		}
		const jiraEnabled = enabledPlatformSync.JIRAStatus === true;
		if (jiraEnabled && ["Placement", "AdSet", "LineItem", "Package"]?.includes(hierarchyLevel)) {
			if (findColumn("Status")) {
				columns.push(
					<ColumnTemplate
						key="TraffickingPlacementStatusIDColumn"
						bind="TraffickingPlacementStatusID"
						sticky={stickyState?.TraffickingPlacementStatusID}
						minWidth={true}
						columnSettings={{ label: "TM Status", canHide: true, defaultHidden: !findColumn("Status")?.selected, defaultSticky: true }}
						onRender={(bindData, rowData, rowIndex) => {
							try {
								const statusConstants = this.TRAFFICKING_STATUS_CONSTANTS[bindData]
									? this.TRAFFICKING_STATUS_CONSTANTS[bindData]
									: this.TRAFFICKING_STATUS_CONSTANTS.default;
								const domEl = `id--trafficking--${rowData.UniqueID}`;
								return rowData.UniqueID ? (
									<TraffickingIcon
										type={statusConstants.type}
										statusMessage={statusConstants.message}
										domElement={domEl}
										position={rowIndex === 0 ? TOOLTIP_POSITION.BOTTOM_START : TOOLTIP_POSITION.AUTO}
									/>
								) : (
									<></>
								);
							} catch (e) {
								console.log(e);
								return <></>;
							}
						}}
					>
						TM Status
					</ColumnTemplate>
				);
			}

			if (enabledPlatformSync.gcmStatus === true && ["Placement", "Package"].includes(hierarchyLevel) && metaData.PlatformID === 1) {
				if (findColumnByName("Google Status")) {
					columns.push(this.returnSyncStatusColumn("Google Status", !findColumnByName("Google Status")?.selected, true, stickyState?.ExternalSystemStatusID));
				}
			}

			if (findColumn("GroupNumber")) {
				columns.push(
					<ColumnTemplate
						key="TraffickingRequestGroupNumbersColumn"
						bind="TraffickingRequestGroupNumbers"
						sticky={stickyState?.TraffickingRequestGroupNumbers}
						minWidth={true}
						columnSettings={{ label: "Group Number", canHide: true, defaultHidden: !findColumn("GroupNumber")?.selected, defaultSticky: true }}
						onRender={(bindData, rowData, rowIndex) => {
							try {
								const domEl = `id--group--number--${rowData.UniqueID}`;
								return bindData ? <GroupNumberIcon groupNumber={bindData} domElement={domEl} /> : <></>;
							} catch (e) {
								console.log(e);
								return <></>;
							}
						}}
					>
						Group Number
					</ColumnTemplate>
				);
			}

			if (findColumn("GooglePlacementID")) {
				columns.push(
					<ColumnTemplate
						key={"GoogleCampaignManagerPKColumn"}
						bind={"GoogleCampaignManagerPK"}
						sticky={stickyState?.GoogleCampaignManagerPK}
						minWidth={true}
						hasRenderCache={false}
						columnSettings={{ label: `Google ${hierarchyLevel} ID`, canHide: true, defaultHidden: !findColumn("GooglePlacementID")?.selected, defaultSticky: true }}
						type={TD_TYPE.NUMBER}
						onRender={(cell, row) => {
							if ((hierarchyLevel === "Placement" || hierarchyLevel === "Package") && enabledPlatformSync.JIRAStatus === true) {
								// Adding this try/catch block in situations where the PlatformPlacementPK is not JSON parsable
								try {
									const targetValue = row[`Platform${hierarchyLevel}PK`]
										? JSON.parse(row[`Platform${hierarchyLevel}PK`])?.find(({ MasterPlatformID }) => MasterPlatformID === 1)?.PlatformPK
										: "";
									return <>{targetValue}</>;
								} catch {
									return <>{row[`Platform${hierarchyLevel}PK`]}</>;
								}
							} else {
								return <></>;
							}
						}}
					>
						Google {hierarchyLevel} ID
					</ColumnTemplate>
				);
			}

			if (findColumn("DoNotUse")) {
				columns.push(buildDNU());
			}
		}

		if (enabledPlatformSync.syncAPI === true) {
			if (hierarchyLevel === "Package" && metaData.PlatformID === 5) {
				if (metaData.ExternalSystemStatusID !== 5) {
					// don't combine these conditions into one line, don't want to fall through to another else/if condition
					if (findColumn("PlatformStatus")) {
						columns.push(this.returnSyncStatusColumn("Prisma Status", !findColumn("PlatformStatus")?.selected, false, stickyState?.ExternalSystemStatusID));
					}
					if (findColumn("PrismaID")) {
						columns.push(this.returnPlatformPKColumn("Prisma ID", "PlatformPackagePK", 5, !findColumn("PrismaID")?.selected, stickyState?.PlatformPackagePK));
					}
				}
			} else if (hierarchyLevel === "InsertionOrder") {
				if (findColumn("PlatformStatus")) {
					columns.push(this.returnSyncStatusColumn("DV360 Status", !findColumn("PlatformStatus")?.selected, false, stickyState?.ExternalSystemStatusID));
				}
				if (findColumn("DV360ID")) {
					columns.push(this.returnPlatformPKColumn("DV360 ID", "PlatformInsertionOrderPK", 2, !findColumn("DV360ID")?.selected, stickyState?.PlatformInsertionOrderPK));
				}
			} else if (hierarchyLevel === "LineItem") {
				if (findColumn("PlatformStatus")) {
					columns.push(this.returnSyncStatusColumn("DV360 Status", !findColumn("PlatformStatus")?.selected, false, stickyState?.ExternalSystemStatusID));
				}
				if (findColumn("DV360ID")) {
					columns.push(this.returnPlatformPKColumn("DV360 ID", "PlatformLineItemPK", 2, !findColumn("DV360ID")?.selected, stickyState?.PlatformLineItemPK));
				}
			} else if (metaData.PlatformID !== 1) {
				columns.push(this.returnSyncStatusColumn("Sync Status", undefined, false, stickyState?.ExternalSystemStatusID));
			}
		}

		if (findColumn("IsDraft")) {
			columns.push(
				<ColumnTemplate
					// this comment exists to prevent eslint from making this unreadable.
					key="IsDraftColumn"
					bind="IsDraft"
					minWidth={true}
					sticky={stickyState?.IsDraft}
					columnSettings={{ defaultSticky: true, label: "Draft Status" }}
					onRender={(v) => (v === "True" ? <Status type={STATUS_TYPE.WARNING} title={"Draft"} /> : <></>)}
				>
					Draft Status
				</ColumnTemplate>
			);
		}
	};

	static returnSyncStatusColumn = (columnName, defaultHidden, isBadge = false, sticky) => {
		return (
			<ColumnTemplate
				key="ExternalSystemStatusIDColumn"
				bind="ExternalSystemStatusID"
				sticky={sticky}
				minWidth={true}
				{...(defaultHidden == null
					? { columnSettings: { defaultSticky: true } }
					: { columnSettings: { label: columnName, canHide: true, defaultHidden, defaultSticky: true } })}
				onRender={(value, rowData, rowIndex) => {
					return (
						<PlatformStatusIcon
							status={parseInt(value, 10)}
							tooltip={rowData?.ExternalSystemResponse}
							isBadge={isBadge}
							position={rowIndex === 0 ? TOOLTIP_POSITION.BOTTOM_START : TOOLTIP_POSITION.AUTO}
						/>
					);
				}}
			>
				{columnName}
			</ColumnTemplate>
		);
	};

	static returnPlatformPKColumn = (columnName, dataFieldName, platformID, defaultHidden, sticky) => {
		return (
			<ColumnTemplate
				key={`${dataFieldName}Column`}
				bind={dataFieldName}
				sticky={sticky}
				minWidth={true}
				columnSettings={{ label: columnName, canHide: true, defaultHidden, defaultSticky: true }}
				onRender={(bindData, rowData, rowIndex) => {
					try {
						const targetValue = bindData ? JSON.parse(bindData)?.find(({ MasterPlatformID }) => MasterPlatformID === platformID) : null;
						return targetValue ? <div>{targetValue.PlatformPK}</div> : <></>;
					} catch (e) {
						console.log(e);
						return <></>;
					}
				}}
			>
				{columnName}
			</ColumnTemplate>
		);
	};
}
